/**
 * OFFICIAL SINGLE SOURCE - Image-Variant Synchronization Logic
 * 
 * Unified image selection system that eliminates competing algorithms between
 * QuickViewModal and ShopifyProductDetail.
 * 
 * Handles all edge cases:
 * - Exact image ID matching
 * - URL normalization and comparison  
 * - Color option matching via altText
 * - Fallback chain with predictable results
 */

import { ShopifyProduct, ShopifyVariant } from '@/hooks/useShopify';
import { productionLogger } from './production-logger';

export interface ImageVariantSyncContext {
  product: ShopifyProduct;
  selectedVariant: ShopifyVariant;
  userImageOverride?: boolean;
  currentImageIndex?: number;
}

export interface ImageSyncResult {
  imageIndex: number;
  imageUrl: string;
  matchMethod: 'exact_id' | 'url_match' | 'color_alt' | 'variant_image' | 'fallback';
  confidence: 'high' | 'medium' | 'low';
}

/**
 * OFFICIAL SINGLE SOURCE - Get optimal image index for variant
 * Used by: QuickViewModal, ShopifyProductDetail, Product Cards
 */
export const getImageIndexForVariant = (context: ImageVariantSyncContext): ImageSyncResult => {
  const { product, selectedVariant, userImageOverride, currentImageIndex } = context;

  // Respect user manual image selection
  if (userImageOverride && currentImageIndex !== undefined && product.images[currentImageIndex]) {
    return {
      imageIndex: currentImageIndex,
      imageUrl: product.images[currentImageIndex].url,
      matchMethod: 'fallback',
      confidence: 'high' // User choice is always high confidence
    };
  }

  if (!product.images || product.images.length === 0) {
    productionLogger.debug('🔍 IMAGE SYNC DEBUG: No product images available');
    return {
      imageIndex: 0,
      imageUrl: '',
      matchMethod: 'fallback',
      confidence: 'low'
    };
  }

  // Try variant image if available
  const variantImage = selectedVariant.image;
  productionLogger.debug('🔍 IMAGE SYNC DEBUG: Variant has image:', !!variantImage);
  
  if (variantImage) {
    productionLogger.debug('🔍 IMAGE SYNC DEBUG: Variant image ID:', variantImage.id);
    productionLogger.debug('🔍 IMAGE SYNC DEBUG: Variant image URL:', variantImage.url);
    
    // Method 1: Exact ID matching (highest confidence)
    productionLogger.debug('🔍 IMAGE SYNC DEBUG: Trying exact ID matching...');
    const exactIdMatch = product.images.findIndex(img => {
      productionLogger.debug('🔍 IMAGE SYNC DEBUG: Comparing', img.id, 'vs', variantImage.id);
      return img.id === variantImage.id;
    });
    
    if (exactIdMatch >= 0) {
      productionLogger.debug('🔍 IMAGE SYNC DEBUG: ✅ Exact ID match found at index:', exactIdMatch);
      return {
        imageIndex: exactIdMatch,
        imageUrl: product.images[exactIdMatch].url,
        matchMethod: 'exact_id',
        confidence: 'high'
      };
    }
    productionLogger.debug('🔍 IMAGE SYNC DEBUG: ❌ No exact ID match found');

    // Method 2: URL matching with normalization
    productionLogger.debug('🔍 IMAGE SYNC DEBUG: Trying URL matching...');
    const urlMatch = findImageByUrlMatch(product.images, variantImage.url);
    if (urlMatch.index >= 0) {
      productionLogger.debug('🔍 IMAGE SYNC DEBUG: ✅ URL match found at index:', urlMatch.index);
      return {
        imageIndex: urlMatch.index,
        imageUrl: product.images[urlMatch.index].url,
        matchMethod: 'url_match',
        confidence: 'high'
      };
    }
    productionLogger.debug('🔍 IMAGE SYNC DEBUG: ❌ No URL match found');
  }

  // Method 3: Color option matching via altText
  productionLogger.debug('🔍 IMAGE SYNC DEBUG: Trying color option matching...');
  const colorMatch = findImageByColorOption(product, selectedVariant);
  if (colorMatch.index >= 0) {
    productionLogger.debug('🔍 IMAGE SYNC DEBUG: ✅ Color match found at index:', colorMatch.index);
    return {
      imageIndex: colorMatch.index,
      imageUrl: product.images[colorMatch.index].url,
      matchMethod: 'color_alt',
      confidence: 'medium'
    };
  }
  productionLogger.debug('🔍 IMAGE SYNC DEBUG: ❌ No color match found');

  // Method 4: Any variant with image
  productionLogger.debug('🔍 IMAGE SYNC DEBUG: Trying any variant image matching...');
  const anyVariantImageMatch = findImageFromAnyVariant(product, selectedVariant);
  if (anyVariantImageMatch.index >= 0) {
    productionLogger.debug('🔍 IMAGE SYNC DEBUG: ✅ Any variant image match found at index:', anyVariantImageMatch.index);
    return {
      imageIndex: anyVariantImageMatch.index,
      imageUrl: product.images[anyVariantImageMatch.index].url,
      matchMethod: 'variant_image',
      confidence: 'medium'
    };
  }
  productionLogger.debug('🔍 IMAGE SYNC DEBUG: ❌ No variant image match found');

  // Method 5: Fallback to first image
  productionLogger.debug('🔍 IMAGE SYNC DEBUG: 🔄 Falling back to first image (index 0)');
  return {
    imageIndex: 0,
    imageUrl: product.images[0].url,
    matchMethod: 'fallback',
    confidence: 'low'
  };
};

/**
 * Find image by URL matching with normalization
 */
const findImageByUrlMatch = (images: any[], targetUrl: string): { index: number; confidence: 'high' | 'medium' } => {
  const normalizeUrl = (url: string) => url.split('?')[0].toLowerCase();
  const normalizedTarget = normalizeUrl(targetUrl);

  // Exact normalized URL match
  let index = images.findIndex(img => normalizeUrl(img.url) === normalizedTarget);
  if (index >= 0) {
    return { index, confidence: 'high' };
  }

  // Filename matching (more flexible)
  const targetFilename = normalizedTarget.split('/').pop();
  if (targetFilename) {
    index = images.findIndex(img => {
      const imgFilename = normalizeUrl(img.url).split('/').pop();
      return imgFilename === targetFilename;
    });
    if (index >= 0) {
      return { index, confidence: 'medium' };
    }
  }

  return { index: -1, confidence: 'medium' };
};

/**
 * Find image by color option matching via altText
 */
const findImageByColorOption = (product: ShopifyProduct, variant: ShopifyVariant): { index: number; confidence: 'medium' | 'low' } => {
  const colorOption = variant.selectedOptions?.find(opt => 
    opt.name?.toLowerCase() === 'color'
  ) || variant.selectedOptions?.[0];

  if (!colorOption?.value) {
    return { index: -1, confidence: 'low' };
  }

  const colorValue = colorOption.value.toLowerCase();
  
  // Direct altText matching
  let index = product.images.findIndex(img => 
    img.altText?.toLowerCase().includes(colorValue)
  );
  
  if (index >= 0) {
    return { index, confidence: 'medium' };
  }

  // Fuzzy color matching (partial matches)
  index = product.images.findIndex(img => {
    const altText = img.altText?.toLowerCase() || '';
    // Check if any word in color value appears in altText
    return colorValue.split(' ').some(word => 
      word.length > 2 && altText.includes(word)
    );
  });

  return { index: index >= 0 ? index : -1, confidence: 'low' };
};

/**
 * Find image from any variant that has an image
 */
const findImageFromAnyVariant = (product: ShopifyProduct, currentVariant: ShopifyVariant): { index: number; confidence: 'medium' } => {
  // Look for other variants with images
  const variantWithImage = product.variants.find(v => 
    v.id !== currentVariant.id && v.image
  );

  if (!variantWithImage?.image) {
    return { index: -1, confidence: 'medium' };
  }

  const index = product.images.findIndex(img => 
    img.id === variantWithImage.image.id ||
    img.url.split('?')[0] === variantWithImage.image.url.split('?')[0]
  );

  return { index: index >= 0 ? index : -1, confidence: 'medium' };
};

/**
 * Get current image URL from product at index
 */
export const getImageAtIndex = (product: ShopifyProduct, index: number): string => {
  if (!product.images || index < 0 || index >= product.images.length) {
    return product.images?.[0]?.url || '';
  }
  return product.images[index].url;
};

/**
 * Validate image-variant synchronization
 */
export const validateImageVariantSync = (
  product: ShopifyProduct, 
  variant: ShopifyVariant, 
  imageIndex: number
): boolean => {
  const syncResult = getImageIndexForVariant({
    product,
    selectedVariant: variant
  });

  return Math.abs(syncResult.imageIndex - imageIndex) <= 1; // Allow 1 index tolerance
};

/**
 * Debug helper - analyze image-variant relationships
 */
export const analyzeImageVariantRelationships = (product: ShopifyProduct) => {
  const analysis = {
    totalImages: product.images?.length || 0,
    totalVariants: product.variants?.length || 0,
    variantsWithImages: 0,
    imageVariantMappings: [] as Array<{
      variantId: string;
      variantTitle: string;
      imageIndex: number;
      matchMethod: string;
      confidence: string;
    }>
  };

  product.variants?.forEach(variant => {
    if (variant.image) analysis.variantsWithImages++;

    const syncResult = getImageIndexForVariant({
      product,
      selectedVariant: variant
    });

    analysis.imageVariantMappings.push({
      variantId: variant.id,
      variantTitle: variant.title,
      imageIndex: syncResult.imageIndex,
      matchMethod: syncResult.matchMethod,
      confidence: syncResult.confidence
    });
  });

  return analysis;
};